import { alpha, createTheme } from "@mui/material/styles";

import type { GroupCategory } from "@thalamos/haven-common";

import "@fontsource/ibm-plex-sans/300.css";
import "@fontsource/ibm-plex-sans/400.css";
import "@fontsource/ibm-plex-sans/500.css";
import "@fontsource/ibm-plex-sans/600.css";
import "@fontsource/ibm-plex-sans/700.css";

/** A fill, and the colour of text drawn on it. */
export type Swatch = { background: string; color: string };

type RatingColours = {
  /** Each kind of domain's wash behind its heading, and its ratings at levels 3 and 2. */
  category: Record<
    GroupCategory,
    { headingBackground: string; level3: Swatch; level2: Swatch }
  >;
  /** Drawn to recede: a rating below level 2, or one that cannot be placed. */
  quiet: Swatch;
  /** A strength to use, 0 strongest, which is a credit rather than a concern. */
  strengthToUse: Record<0 | 1, Swatch>;
};

/** The tones an item's change, or its course across a range, is marked in. */
type ChangeColours = Record<
  "muted" | "warm" | "hot" | "cool" | "rust" | "blank",
  Swatch
>;

/**
 * Each charted kind of domain's line, and the mark for an event. The lines were
 * checked together for separation in normal and colour-blind vision on white;
 * another line would need checking again. History is grey on purpose: it is
 * context, never something to act on.
 */
type ChartColours = Record<"needs" | "strengths" | "history" | "event", string>;

/**
 * Soft fills in a palette colour's hue, for a label that informs rather than
 * calls for action, each with the colour of an icon drawn on it.
 */
type TonalColours = Record<"primary" | "neutral", Swatch & { icon: string }>;

/** The drawer's items, drawn over the drawer's own background. */
type DrawerColours = {
  /** Marks the section being looked at; also fills the account avatar. */
  activeItem: string;
  /** Lighter than `activeItem`, so hovering never reads as being there. */
  hoveredItem: string;
};

declare module "@mui/material/styles" {
  interface Palette {
    /** Draws the eye without reading as an error. A fill, never a text colour. */
    attention: Palette["primary"];
    rating: RatingColours;
    change: ChangeColours;
    chart: ChartColours;
    drawer: DrawerColours;
    tonal: TonalColours;
  }
  interface PaletteOptions {
    attention?: PaletteOptions["primary"];
    rating?: RatingColours;
    change?: ChangeColours;
    chart?: ChartColours;
    drawer?: DrawerColours;
    tonal?: TonalColours;
  }
  interface TypeBackground {
    /** Supporting surfaces, such as alternate rows and section backgrounds. */
    subtle: string;
    /** Behind the banner that heads a page about one person. */
    personBanner: string;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    attention: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    attention: true;
  }
}

const shades800 = "#0B122C";
const primaryMain = "#205D89";

export const theme = createTheme({
  palette: {
    // WCAG AA for text, where MUI's default only reaches 3:1.
    contrastThreshold: 4.5,
    primary: { main: primaryMain },
    secondary: { main: "#0876D1" },
    error: { main: "#D33F1D", light: "#E26D5A" },
    // `success` is MUI's own. The design draws white text on `info` and
    // `warning` fills, which falls short of `contrastThreshold`, so their
    // `contrastText` is set here rather than worked out.
    info: { main: "#0288D1", contrastText: "#FFFFFF" },
    warning: { main: "#EC501C", contrastText: "#FFFFFF" },
    // The design's yellow cannot be read as text, so it is `attention` rather
    // than `warning`.
    attention: {
      main: "#FCCA40",
      light: "#F0DC82",
      dark: "#B08D2C",
      contrastText: shades800,
    },
    // Shades 800 at each percentage the design gives, flattened onto white,
    // because MUI ignores transparency when it works out contrast.
    text: { primary: shades800, secondary: "#54596B" },
    divider: "#E2E3E6",
    background: {
      default: "#FCFCFC",
      paper: "#FFFFFF",
      subtle: "#FAFAFB",
      personBanner: "#F6F9FD",
    },
    rating: {
      category: {
        history: {
          headingBackground: "#F4F5F7",
          level3: { background: "#4B5058", color: "#FFFFFF" },
          level2: { background: "#A3A8AF", color: "#1F2328" },
        },
        needs: {
          headingBackground: "#FBEEE4",
          level3: { background: "#A14E1F", color: "#FFFFFF" },
          level2: { background: "#F2BA8C", color: "#3D2414" },
        },
        strengths: {
          headingBackground: "#EDF1F9",
          level3: { background: "#93AAD8", color: "#16264A" },
          level2: { background: "#CBD7EF", color: "#16264A" },
        },
        caregiver: {
          headingBackground: "#F2EEF8",
          level3: { background: "#6B4C9A", color: "#FFFFFF" },
          level2: { background: "#CDBFE6", color: "#2A1D40" },
        },
      },
      quiet: { background: "#E6E8EC", color: "#3A3F45" },
      strengthToUse: {
        0: { background: "#2A4479", color: "#FFFFFF" },
        1: { background: "#4767AE", color: "#FFFFFF" },
      },
    },
    change: {
      muted: { background: "#EEF0F2", color: "#3A3F45" },
      warm: { background: "#FBE7D6", color: "#7A3510" },
      hot: { background: "#FADDD8", color: "#86231A" },
      cool: { background: "#E1E8F6", color: "#1F3E7A" },
      rust: { background: "#A14E1F", color: "#FFFFFF" },
      blank: { background: "#FFFFFF", color: "#3A3F45" },
    },
    chart: {
      needs: "#C4622D",
      strengths: "#3D6BB3",
      history: "#8A9099",
      event: "#A14E1F",
    },
    // Translucent white, so the items lighten whatever colour the drawer is.
    drawer: {
      activeItem: alpha("#FFFFFF", 0.18),
      hoveredItem: alpha("#FFFFFF", 0.1),
    },
    tonal: {
      primary: { background: "#E7EDF5", color: primaryMain, icon: "#1D4E79" },
      neutral: { background: "#EDEEF0", color: "#4B5563", icon: "#6B7280" },
    },
  },
  typography: {
    // TODO(branding): the design's text sizes on MUI's variants, once confirmed.
    fontFamily: "IBM Plex Sans, sans-serif",
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
  },
  components: {
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({ padding: theme.spacing(3, 3, 2) }),
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }) => ({ color: theme.palette.text.primary }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        // The way out sits on the left and the action on the right; a button
        // alone, such as Close, sits on the right.
        root: ({ theme }) => ({
          padding: theme.spacing(2, 3, 3),
          justifyContent: "space-between",
          "& > :only-child": { marginLeft: "auto" },
        }),
      },
    },
  },
});
